import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Check, ChevronsUpDown, Building2, Plus, Mail } from "lucide-react"
import { useOrgs } from '@/contexts/OrgsContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { NewOrganizationDialog } from "./NewOrganizationDialog"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"

/**
 * Organization switcher component for sidebar
 * Displays current organization and allows switching between user's organizations
 */
export function OrganizationSwitcher() {
  const navigate = useNavigate()
  const {
    activeOrgs,
    pendingInvites,
    selectedOrganizationId,
    selectedOrganization,
    setSelectedOrganizationId,
    acceptInvitation,
    isProcessingInvitation,
    isLoading,
  } = useOrgs()
  const { t } = useLanguage()
  const [newOrgDialogOpen, setNewOrgDialogOpen] = useState(false)

  /**
   * Handle organization selection
   * Updates the selected organization and navigates to dashboard
   */
  const handleOrgSelect = (orgId: string) => {
    setSelectedOrganizationId(orgId)
    void navigate('/dashboard')
  }

  if (isLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
              <Skeleton className="size-4" />
            </div>
            <div className="flex flex-col gap-0.5 leading-none flex-1">
              <Skeleton className="h-4 w-[120px]" />
              <Skeleton className="h-3 w-[80px]" />
            </div>
            <Skeleton className="ml-auto size-4" />
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  /**
   * Accept a pending invitation; the organization becomes selectable once the list refreshes
   */
  const handleAcceptInvitation = (orgId: string) => {
    acceptInvitation(orgId).catch(() => {
      // acceptInvitation already reports the failure to the user
    })
  }

  if (activeOrgs.length === 0 && pendingInvites.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg" disabled>
            <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
              <Building2 className="size-4" />
            </div>
            <div className="flex flex-col gap-0.5 leading-none">
              <span className="font-medium">{t.sidebar.organizationSwitcher.noOrganizations}</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
                  <Building2 className="size-4" />
                </div>
                <div className="flex flex-col gap-0.5 leading-none min-w-0">
                  <span className="font-medium truncate">{selectedOrganization?.orgName ?? t.sidebar.organizationSwitcher.organization}</span>
                  <span className="text-xs text-muted-foreground truncate">
                    {activeOrgs.length} {activeOrgs.length === 1 ? t.sidebar.organizationSwitcher.organizationSingular : t.sidebar.organizationSwitcher.organizationPlural}
                  </span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width]"
              align="start"
            >
              {activeOrgs.map((org) => (
                <DropdownMenuItem
                  key={org.orgId}
                  onSelect={() => { handleOrgSelect(org.orgId); }}
                >
                  {org.orgName}
                  {org.orgId === selectedOrganizationId && <Check className="ml-auto" />}
                </DropdownMenuItem>
              ))}
              {pendingInvites.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {t.sidebar.organizationSwitcher.pendingInvitations}
                  </DropdownMenuLabel>
                  {pendingInvites.map((invite) => (
                    <DropdownMenuItem
                      key={invite.orgId}
                      disabled={isProcessingInvitation}
                      onSelect={() => { handleAcceptInvitation(invite.orgId); }}
                    >
                      <Mail className="mr-2 size-4" />
                      <span className="truncate">{invite.orgName}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {t.sidebar.organizationSwitcher.acceptInvitation}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => { setNewOrgDialogOpen(true); }}>
                <Plus className="mr-2 size-4" />
                {t.sidebar.organizationSwitcher.newOrganization}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <NewOrganizationDialog
        open={newOrgDialogOpen}
        onOpenChange={setNewOrgDialogOpen}
      />
    </>
  )
}
